'use client';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from '@mui/material';
import { mergeSx as mergeSxUtilities } from 'merge-sx';
import React, { useEffect } from 'react';

import { AbgTypography, CloseIcon, mergeSx } from '@abg/kit-base';

import { isIosSafari } from './utils/action-dialog.utils';
import {
  actionButtonStyling,
  closeIconStyling,
  dialogActionsStyles,
  dialogContentStyling,
  dialogDescriptionStyling,
  dialogStyling,
  dialogTitleStyling,
  iconWrapperStyling,
  printDivider,
} from './ActionDialog.styling';
import { ActionDialogProps } from './types';

export const ActionDialog: React.FC<ActionDialogProps> = ({
  styleParts = {},
  isOpen,
  title,
  description,
  children,
  primaryActionLabel,
  onPrimaryAction,
  onClose,
  divider = true,
  primaryActionDisabled,
}) => {
  //TODO: Add loading state handling - need discussion with design team
  //iOS Safari specific behavior where background scrolling is disabled
  useEffect(() => {
    if (!isIosSafari()) return; // only run for Safari on iOS

    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo(0, scrollY);
      };
    }
    return;
  }, [isOpen]);
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      sx={mergeSx(dialogStyling, styleParts?.actionDialog?.dialogStyling)}
      data-testid="action-dialog"
      disableScrollLock={false}
    >
      <DialogTitle
        component="h6"
        sx={mergeSx(
          dialogTitleStyling,
          styleParts.actionDialog?.dialogTitleStyling,
        )}
        data-testid="action-dialog-title"
      >
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={iconWrapperStyling}
        >
          <CloseIcon sx={closeIconStyling} />
        </IconButton>
      </DialogTitle>
      {divider && <Divider sx={printDivider} />}
      <DialogContent
        sx={mergeSx(
          dialogContentStyling,
          styleParts?.actionDialog?.dialogContentStyling,
        )}
        data-testid="action-dialog-content"
      >
        {description && (
          <AbgTypography
            variant="bodyRegularRegular"
            component="div"
            sx={mergeSxUtilities(
              dialogDescriptionStyling,
              styleParts?.actionDialog?.dialogDescriptionStyling,
            )}
            data-testid="action-dialog-description"
          >
            {description}
          </AbgTypography>
        )}
        {children}
      </DialogContent>
      <DialogActions
        sx={mergeSxUtilities(
          dialogActionsStyles,
          styleParts?.actionDialog?.dialogActionsStyles,
        )}
        data-testid="action-dialog-footer"
      >
        <Button
          onClick={onPrimaryAction}
          sx={mergeSxUtilities(
            actionButtonStyling,
            styleParts?.actionDialog?.actionButtonStyling,
          )}
          variant="contained"
          color="primary"
          disabled={primaryActionDisabled}
        >
          {primaryActionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
